import { FC, useCallback, useMemo, useRef } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { getDynamicCacheKey } from 'utils/helper';

// 在业务逻辑中, 很多时候会存在需要把某个页面的数据临时缓存, 然后到之后的某个页面在提交的情况, 这个文件中的hook就是用在此类业务场景中

interface StorageDataContext<T> {
    // 获取总的缓存数据, 默认会把数据拍平到一个对象中, 如果不需要拍平, 就传入noFlat为true
    getTotalStorageData: (params?: { noFlat?: boolean }) => T;
    // 清除缓存数据
    clearStorageData: () => void;
    // 根据key获取缓存数据
    getStorageDataByKey: <K = any>(key: keyof T) => K;
    // 根据key设置缓存数据
    setStorageDataByKey: (key: keyof T, value: any) => void;
}

interface StorageDataContextByKey<T, K extends keyof T> {
    getStorageData: () => T[K];
    setStorageData: (value: T[K]) => void;
    clearStorageData: StorageDataContext<T>['clearStorageData'];
    // 获取总的缓存数据, 默认会把数据拍平到一个对象中, 如果不需要拍平, 就传入noFlat为true
    getTotalStorageData: (params?: { noFlat?: boolean }) => any;
}

/**
 * @description 页面中获取缓存数据的时候可以用此hook
 * 此hook没有绑定key, 适用于需要在当前页面操纵别的key对应的缓存的情况
 */
export function useStorageData<T = any>() {
    return useOutletContext<StorageDataContext<T>>();
}

/**
 * @description 该hook是useStorageData的一个绑定了当前页面key的版本
 */
export function useStorageDataByKey<T, K extends keyof T>(key: keyof T) {
    const { setStorageDataByKey, getStorageDataByKey, clearStorageData, getTotalStorageData } = useStorageData<T>();
    const getStorageData = useCallback<StorageDataContextByKey<T, K>['getStorageData']>(
        () => getStorageDataByKey(key),
        [key, getStorageDataByKey]
    );
    const setStorageData = useCallback<StorageDataContextByKey<T, K>['setStorageData']>(
        value => setStorageDataByKey(key, value),
        [key, setStorageDataByKey]
    );

    return {
        setStorageData,
        getStorageData,
        getTotalStorageData,
        clearStorageData
    };
}

/**
 * @description 一个通用的, 用于页面缓存数据的hooks
 * @param {string} cacheKeyPrefix 缓存key的前缀
 */
function useInitStorageData<T>(cacheKeyPrefix: string) {
    const cacheKey = useRef(getDynamicCacheKey(cacheKeyPrefix));
    const totalStorageDataRef = useRef(
        (() => {
            let params: any = {};
            const totalStorageData = sessionStorage.getItem(cacheKey.current);

            if (totalStorageData) {
                try {
                    params = JSON.parse(totalStorageData);
                } catch {}
            }

            return params;
        })()
    );
    const clearStorageData = useCallback(() => {
        sessionStorage.removeItem(cacheKey.current);
        totalStorageDataRef.current = {};
    }, []);
    const getStorageDataByKey = useCallback<StorageDataContext<T>['getStorageDataByKey']>(
        key => totalStorageDataRef.current[key],
        []
    );
    const getTotalStorageData = useCallback<StorageDataContext<T>['getTotalStorageData']>(params => {
        const { noFlat } = params || {};

        // 如果不需要拍平, 就返回原数据
        if (noFlat) {
            return totalStorageDataRef.current;
        }

        return Object.values(totalStorageDataRef.current).reduce<any>(
            (result, stepParams) => (typeof stepParams !== 'string' ? Object.assign(result, stepParams) : result),
            {}
        );
    }, []);
    const setStorageDataByKey = useCallback<StorageDataContext<T>['setStorageDataByKey']>(
        (key, value) => {
            totalStorageDataRef.current = { ...totalStorageDataRef.current, [key]: value };

            sessionStorage.setItem(cacheKey.current, JSON.stringify(totalStorageDataRef.current));
        },
        [cacheKey]
    );
    const value = useMemo<StorageDataContext<T>>(
        () => ({
            clearStorageData,
            getStorageDataByKey,
            setStorageDataByKey,
            getTotalStorageData
        }),
        [clearStorageData, getStorageDataByKey, setStorageDataByKey, getTotalStorageData]
    );

    return value;
}

// 使用缓存的时候挂载在Route上的element
export const StorageRouteElement: FC<{ cacheKey: string }> = ({ cacheKey }) => {
    const storageDataContext = useInitStorageData(`_STORAGE_OF_${cacheKey}_`);

    return <Outlet context={storageDataContext} />;
};
